import { Injectable, inject } from '@angular/core';
import { AuthService } from '@nomoh-monorepo/data-access-auth';
import { WhatsappConfigService } from '@nomoh-monorepo/data-access-bardo-administrador';
import { AgendaService } from '../feature-agenda/agenda.service';
import {
  BardoContextDataService,
  temEstabelecimentoNaSessao,
} from '../feature-bardo-context/bardo-context.dataservice';
import { DashboardService } from '../feature-dashboard/dashboard.service';
import { EstabelecimentoService } from '../feature-estabelecimento/estabelecimento.service';
import { ProfissionalService } from '../feature-profissional/profissional.service';
import { TipoServicoService } from '../feature-tipo-servico/tipo-servico.service';
import { WhatsappConfigDataService } from '../feature-whatsapp-config/whatsapp-config.dataservice';

/**
 * Bootstrap dos DataServices do Bardo Admin após autenticação / primeiro acesso.
 * Substitui o antigo PrimeiroAcessoDataService.$primeiroAcessoConcluido.
 */
@Injectable({ providedIn: 'root' })
export class BardoBootstrapService {
  private readonly authService = inject(AuthService);
  private readonly agendaService = inject(AgendaService);
  private readonly tipoServicoService = inject(TipoServicoService);
  private readonly profissionalService = inject(ProfissionalService);
  private readonly estabelecimentoService = inject(EstabelecimentoService);
  private readonly bardoContextDataService = inject(BardoContextDataService);
  private readonly dashboardService = inject(DashboardService);
  private readonly whatsappConfigDataService = inject(WhatsappConfigDataService);
  private whatsappConfigService = inject(WhatsappConfigService);

  private inicializado = false;

  /**
   * No login: só inicializa se o JWT já trouxe estabelecimento.
   * Sem esse id, o contexto fica com o id da pessoa (onboarding).
   */
  inicializarSeElegivel(): void {
    const temEstabelecimento = temEstabelecimentoNaSessao(
      this.authService.$bardoContextoAtivo(),
      this.authService.$user()?.id
    );

    if (!temEstabelecimento) {
      return;
    }

    this.inicializar();
  }

  /**
   * Força o bootstrap (ex.: logo após salvar o estabelecimento no primeiro acesso).
   */
  inicializar(force = false): void {
    if (this.inicializado && !force) {
      return;
    }

    this.inicializado = true;

    this.agendaService.buscarAgenda();
    this.agendaService.buscarFeriados();
    this.inicializarPorPerfil();
  }

  private inicializarPorPerfil(): void {
    void this.estabelecimentoService.getPlanoQuotas().catch(() => {
      // Quotas são opcionais no bootstrap; a UI trata ausência.
    });

    const perfilAtivo = this.bardoContextDataService.$perfilAtivo();

    if (perfilAtivo === 'ADMINISTRADOR') {
      this.estabelecimentoService.findCompleto().then((estabelecimento) => {
        this.whatsappConfigDataService.init();
        this.whatsappConfigService.testarConexao(
          this.whatsappConfigDataService.$sessionName()
        );
      });
      this.tipoServicoService.findAll();
      this.profissionalService.findAll({});
      this.dashboardService.buscarResumo();
    } else if (perfilAtivo === 'RECEPCAO') {
      this.estabelecimentoService.findCompleto().then((estabelecimento) => {
        this.whatsappConfigDataService.init();
        this.whatsappConfigService.testarConexao(
          this.whatsappConfigDataService.$sessionName()
        );
      });
      this.profissionalService.findAll({});
      this.dashboardService.buscarResumoRecepcao();
    } else if (perfilAtivo === 'PROFISSIONAL') {
      this.dashboardService.buscarResumoProfissional();
    }

    // Testa a conexão do WhatsApp sem precisar entrar na tela de configuração

  }

  /** Chamado no logout para permitir novo bootstrap na próxima sessão. */
  reset(): void {
    this.inicializado = false;
  }
}
